import { useEffect, useState, useRef } from "react";
import { DatePickerCalendar } from "react-nice-dates";
import { isWithinInterval } from "date-fns";
import "react-nice-dates/build/style.css";
import "./styles.css";
import {
  getDay,
  startOfDay,
  isBefore,
  isAfter,
  addMinutes,
  addDays,
  addMonths,
  format,
  parseISO,
  isSameDay,
  startOfMonth,
  endOfDay,
} from "date-fns";
import { es } from "date-fns/locale";
import { apiGet, apiPost, resolveApiBase, LOCAL } from "../../api/client";
import { Link } from "react-router-dom";
import {
  IconScissors,
  IconClock,
  IconCalendar,
  IconUser,
  IconTag,
  IconCheck,
  IconCheckCircle,
  IconBell,
  IconArrowLeft,
  IconWhatsApp,
  IconMail,
  IconPhone,
} from "../icons/Icons";

const WHATSAPP_URL = "https://wa.me/640997403";

const STEPS = [
  "Profesional",
  "Servicio",
  "Día",
  "Hora",
  "Tus datos",
  "Resumen",
];

function capitalize(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

function Reservas() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedHour, setSelectedHour] = useState(null);
  const [availableHours, setAvailableHours] = useState([]);
  const [haircutTypes, setHaircutTypes] = useState([]);
  const [selectedServiceId, setSelectedServiceId] = useState(null);
  const [selectedServiceDuration, setSelectedServiceDuration] = useState(30);
  const [form, setForm] = useState({ name: "", email: "", number: "" });
  const [bookingDetails, setBookingDetails] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [disabledDates, setDisabledDates] = useState([]);
  const [fullyBookedDates, setFullyBookedDates] = useState([]);
  const [displayedMonth, setDisplayedMonth] = useState(new Date());
  const [isLoadingMonth, setIsLoadingMonth] = useState(false);
  const [loadingDisabledDates, setLoadingDisabledDates] = useState(false);
  const [bookingsByWorker, setBookingsByWorker] = useState({});
  const [firstAvailByWorker, setFirstAvailByWorker] = useState({});
  const [workerLoadedCount, setWorkerLoadedCount] = useState({});
  const [hoursError, setHoursError] = useState(false);
  const [servicesError, setServicesError] = useState(false);
  const [confirmError, setConfirmError] = useState(null);
  const loadedMonthsRef = useRef(new Set());

  const workers = [
    {
      id: 1,
      name: "Eva Flores",
      role: "Barbera",
      initials: "EF",
      img: `${process.env.PUBLIC_URL}/Eva.jpeg`,
    },
    {
      id: 3,
      name: "Pol Garcia",
      role: "Barbero",
      initials: "PG",
      img: `${process.env.PUBLIC_URL}/Pol.jpeg`,
    },
  ];

  const Avatar = (w, sm = false) => (
    <span className={`rv-avatar${sm ? " sm" : ""}`}>
      {w?.img ? <img src={w.img} alt={w.name} /> : w?.initials}
    </span>
  );

  const toISO = (s) => s.replace(" ", "T");

  const [selectedWorker, setSelectedWorker] = useState(workers[0].id);

  const SERGI_ID = 2;
  const SERGI_BLOCK_FROM = startOfDay(new Date(2026, 0, 1));
  const POL_ID = 3;
  const POL_AVAILABLE_FROM = startOfDay(new Date(2026, 5, 1));

  const selectedService = haircutTypes.find((s) => s.id === selectedServiceId);
  const selectedWorkerObj = workers.find((w) => w.id === selectedWorker);

  useEffect(() => {
    resolveApiBase();
    getDisabledDates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    recomputeFullyBookedDates(bookingsByWorker[selectedWorker] || {}, selectedServiceDuration);
    if (selectedDate && !hoursError) filterAvailableHours(bookings);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedServiceDuration, bookingsByWorker, selectedWorker, selectedDate, bookings, hoursError]);

  useEffect(() => {
    ensureWorkerMonth(selectedWorker, displayedMonth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWorker, displayedMonth]);

  useEffect(() => {
    if (selectedDate) fetchReservations(selectedDate, selectedWorker);
  }, [selectedWorker, selectedDate]);

  useEffect(() => {
    getServices(selectedWorker);
  }, [selectedWorker]);

  const getDisabledDates = async () => {
    try {
      setLoadingDisabledDates(true);
      const { data } = await apiGet(`/close/local/${LOCAL}`, {
        context: "reservas_dias_cerrados",
      });
      const formatted = data.map((d) => ({
        start: startOfDay(parseISO(d.datetime_init)),
        end: endOfDay(parseISO(d.datetime_end)),
      }));
      setDisabledDates(formatted);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingDisabledDates(false);
    }
  };

  const recomputeFullyBookedDates = (byDay, duration) => {
    const fulls = Object.entries(byDay).reduce((acc, [dayKey, slots]) => {
      const dayDate = startOfDay(parseISO(dayKey));
      const possible = getAvailableHours(dayDate);
      const available = possible.filter((time) => {
        const [hh, mm] = time.split(":").map(Number);
        const start = new Date(
          dayDate.getFullYear(),
          dayDate.getMonth(),
          dayDate.getDate(),
          hh,
          mm,
        );
        const end = addMinutes(start, duration || 30);
        return !slots.some(
          ({ init, end: bookedEnd }) => start < bookedEnd && end > init,
        );
      });
      if (available.length === 0) acc.push(dayDate);
      return acc;
    }, []);
    setFullyBookedDates(fulls);
  };

  const fetchReservations = (date, workerId = selectedWorker) => {
    setLoading(true);
    setHoursError(false);
    const dateForApi = format(date, "yyyy-MM-dd");
    apiGet(`/booking/local/${LOCAL}?date=${dateForApi}&worker_id=${workerId}`, {
      context: "reservas_horas_dia",
    })
      .then((response) => {
        const data = response.data;
        setBookings(Array.isArray(data.bookings) ? data.bookings : []);
      })
      .catch((error) => {
        console.error("Error al obtener las reservas:", error);
        // Sin datos reales no se muestran horas: evita ofrecer huecos ya ocupados
        setBookings([]);
        setAvailableHours([]);
        setSelectedHour(null);
        setHoursError(true);
      })
      .finally(() => setLoading(false));
  };

  const getServices = (workerId = selectedWorker) => {
    setServicesError(false);
    apiGet(`/work_group/${workerId}/services`, { context: "reservas_servicios" })
      .then((response) => {
        const list = response.data.services || [];
        setHaircutTypes(list);
      })
      .catch((error) => {
        console.error("Error al obtener los tipos de corte:", error);
        setHaircutTypes([]);
        setServicesError(true);
      });
  };

  const getOpeningWindows = (date, workerId = selectedWorker) => {
    const day = date.getDay();
    const mk = (h, m = 0) =>
      new Date(date.getFullYear(), date.getMonth(), date.getDate(), h, m);

    if (day === 0) return [];

    if (workerId === 2) {
      if (day >= 1 && day <= 5) return [{ start: mk(17, 0), end: mk(21, 0) }];
      if (day === 6) return [{ start: mk(9, 0), end: mk(13, 0) }];
      return [];
    }

    if (day === 1) return [{ start: mk(17, 0), end: mk(21, 0) }];
    if (day >= 2 && day <= 4)
      return [
        { start: mk(9, 0), end: mk(13, 30) },
        { start: mk(17, 0), end: mk(21, 0) },
      ];
    if (day === 5)
      return [
        // Pol (segundo peluquero) empieza los viernes a las 9:00; Eva a las 9:30
        { start: workerId === POL_ID ? mk(9, 0) : mk(9, 30), end: mk(13, 30) },
        { start: mk(17, 0), end: mk(21, 0) },
      ];
    if (day === 6) return [{ start: mk(9, 0), end: mk(13, 30) }];
    return [];
  };

  const filterAvailableHours = (bookings) => {
    let hours = getAvailableHours(selectedDate);
    let duration = selectedServiceDuration === 0 ? 30 : selectedServiceDuration;

    bookings.forEach((booking) => {
      const startTime = parseISO(toISO(booking.datetime_init));
      const endTime = parseISO(toISO(booking.datetime_end));
      hours = hours.filter((hour) => {
        const [hh, mm] = hour.split(":").map(Number);
        const hourTime = new Date(
          selectedDate.getFullYear(),
          selectedDate.getMonth(),
          selectedDate.getDate(),
          hh,
          mm,
        );
        const hourEndTime = addMinutes(hourTime, duration);
        return hourEndTime <= startTime || hourTime >= endTime;
      });
    });

    setAvailableHours(hours);
  };

  const isDayDisabled = (d) => {
    const today = startOfDay(new Date());
    if (selectedWorker === SERGI_ID && !isBefore(d, SERGI_BLOCK_FROM)) return true;
    if (selectedWorker === POL_ID && isBefore(d, POL_AVAILABLE_FROM)) return true;
    if (
      disabledDates.some((rng) =>
        isWithinInterval(d, { start: rng.start, end: rng.end }),
      )
    )
      return true;
    if (fullyBookedDates.some((fd) => isSameDay(fd, d))) return true;
    if (isBefore(d, today) || getDay(d) === 0) return true;
    if (getOpeningWindows(d, selectedWorker).length === 0) return true;
    if (getAvailableHours(d).length === 0) return true;
    return false;
  };

  const handleDateChange = (d) => {
    if (isDayDisabled(d)) return;
    setLoading(true);
    setAvailableHours([]);
    setSelectedHour(null);
    setDate(d);
    setSelectedDate(d);
    fetchReservations(d, selectedWorker);
  };

  const getAvailableHours = (date) => {
    const today = startOfDay(new Date());
    const now = new Date();
    const isToday = isSameDay(date, today);

    if (selectedWorker === SERGI_ID && !isBefore(date, SERGI_BLOCK_FROM)) return [];
    if (selectedWorker === POL_ID && isBefore(date, POL_AVAILABLE_FROM)) return [];

    const SLOT_STEP =
      selectedWorker === 2 ? 15 : selectedWorker === POL_ID ? 60 : 30;

    const generateTimeSlots = (
      startHour,
      endHour,
      lastMinute = 0,
      step = 30,
      startMinute = 0,
    ) => {
      let times = [];
      // Alinea la rejilla al minuto de apertura: ventana de 9:30 con paso de
      // 60 min genera 9:30, 10:30... en vez de perder la primera media hora
      const offset = startMinute % step;
      for (let hour = startHour; hour < endHour; hour++) {
        for (let minute = offset; minute < 60; minute += step) {
          if (hour === endHour - 1 && minute > lastMinute) break;
          times.push(
            `${hour.toString().padStart(2, "0")}:${minute
              .toString()
              .padStart(2, "0")}`,
          );
        }
      }
      return times;
    };

    const windows = getOpeningWindows(date, selectedWorker);
    if (windows.length === 0) return [];

    const duration =
      (selectedServiceDuration === 0 ? 30 : selectedServiceDuration) || 30;

    let times = [];
    windows.forEach((w) => {
      const base = generateTimeSlots(
        w.start.getHours(),
        w.end.getHours(),
        59,
        SLOT_STEP,
        w.start.getMinutes(),
      );
      const kept = base.filter((time) => {
        const [hh, mm] = time.split(":").map(Number);
        const start = new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
          hh,
          mm,
        );
        const end = addMinutes(start, duration);
        return start >= w.start && end <= w.end;
      });
      times = times.concat(kept);
    });

    if (isToday) {
      times = times.filter((time) => {
        const [hours, minutes] = time.split(":").map(Number);
        const timeDate = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate(),
          hours,
          minutes,
        );
        return isAfter(timeDate, now);
      });
    }

    return times;
  };

  const getFirstAvailableDate = () => {
    let d = startOfDay(new Date());
    for (let i = 0; i < 120; i++) {
      if (!isDayDisabled(d)) return d;
      d = addDays(d, 1);
    }
    return null;
  };

  // --- disponibilidad parametrizada por barbero (para "proxima cita") ---
  const slotsForDay = (date, workerId, duration = 30) => {
    const today = startOfDay(new Date());
    const now = new Date();
    const isToday = isSameDay(date, today);
    if (workerId === SERGI_ID && !isBefore(date, SERGI_BLOCK_FROM)) return [];
    if (workerId === POL_ID && isBefore(date, POL_AVAILABLE_FROM)) return [];
    const SLOT_STEP = workerId === 2 ? 15 : workerId === POL_ID ? 60 : 30;
    const windows = getOpeningWindows(date, workerId);
    if (!windows.length) return [];
    const gen = (sh, eh, lastMin, step, sm = 0) => {
      let t = [];
      const offset = sm % step;
      for (let h = sh; h < eh; h++)
        for (let m = offset; m < 60; m += step) {
          if (h === eh - 1 && m > lastMin) break;
          t.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
        }
      return t;
    };
    let times = [];
    windows.forEach((w) => {
      const base = gen(
        w.start.getHours(),
        w.end.getHours(),
        59,
        SLOT_STEP,
        w.start.getMinutes(),
      );
      times = times.concat(
        base.filter((time) => {
          const [hh, mm] = time.split(":").map(Number);
          const start = new Date(date.getFullYear(), date.getMonth(), date.getDate(), hh, mm);
          const end = addMinutes(start, duration);
          return start >= w.start && end <= w.end;
        }),
      );
    });
    if (isToday) {
      times = times.filter((time) => {
        const [hh, mm] = time.split(":").map(Number);
        return isAfter(
          new Date(today.getFullYear(), today.getMonth(), today.getDate(), hh, mm),
          now,
        );
      });
    }
    return times;
  };

  const dayHasFreeSlot = (d, workerId, byDay) => {
    const today = startOfDay(new Date());
    if (isBefore(d, today) || getDay(d) === 0) return false;
    if (disabledDates.some((r) => isWithinInterval(d, { start: r.start, end: r.end })))
      return false;
    const slots = slotsForDay(d, workerId, 30);
    if (!slots.length) return false;
    const booked = (byDay && byDay[format(d, "yyyy-MM-dd")]) || [];
    return slots.some((time) => {
      const [hh, mm] = time.split(":").map(Number);
      const start = new Date(d.getFullYear(), d.getMonth(), d.getDate(), hh, mm);
      const end = addMinutes(start, 30);
      return !booked.some((b) => start < b.end && end > b.init);
    });
  };

  const firstAvailableFor = (workerId, byDay) => {
    let d = startOfDay(new Date());
    for (let i = 0; i < 120; i++, d = addDays(d, 1)) {
      if (dayHasFreeSlot(d, workerId, byDay)) return d;
    }
    return null;
  };

  const fetchWorkerMonth = (workerId, monthDate) => {
    const dateForApi = format(monthDate, "yyyy-MM-dd");
    setIsLoadingMonth(true);
    apiGet(`/booking/local/${LOCAL}/month?date=${dateForApi}&worker_id=${workerId}`, {
      context: "reservas_mes_barbero",
    })
      .then((res) => {
        const byDay = {};
        (res.data.bookings || []).forEach((b) => {
          const init = parseISO(b.datetime_init);
          const end = parseISO(b.datetime_end);
          (byDay[format(init, "yyyy-MM-dd")] ||= []).push({ init, end });
        });
        setBookingsByWorker((prev) => ({
          ...prev,
          [workerId]: { ...(prev[workerId] || {}), ...byDay },
        }));
      })
      .catch((e) => console.error("Error reservas barbero", e))
      .finally(() => {
        setWorkerLoadedCount((prev) => ({
          ...prev,
          [workerId]: (prev[workerId] || 0) + 1,
        }));
        setIsLoadingMonth(false);
      });
  };

  // Pide el mes de un barbero solo si no esta ya cacheado
  const ensureWorkerMonth = (workerId, monthDate) => {
    const key = `${workerId}-${format(monthDate, "yyyy-MM")}`;
    if (loadedMonthsRef.current.has(key)) return;
    loadedMonthsRef.current.add(key);
    fetchWorkerMonth(workerId, monthDate);
  };

  useEffect(() => {
    // precarga 3 meses por barbero (primer dia disponible + cache del calendario)
    workers.forEach((w) => {
      for (let m = 0; m < 3; m++) ensureWorkerMonth(w.id, addMonths(new Date(), m));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setFirstAvailByWorker((prev) => {
      const next = { ...prev };
      workers.forEach((w) => {
        if ((workerLoadedCount[w.id] || 0) >= 3) {
          next[w.id] = firstAvailableFor(w.id, bookingsByWorker[w.id] || {}) || null;
        }
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingsByWorker, workerLoadedCount, disabledDates]);

  const longDateOf = (d) =>
    d ? capitalize(format(d, "EEEE d 'de' MMMM", { locale: es })) : null;

  // Al entrar en el paso del calendario, salta al mes del primer dia disponible
  useEffect(() => {
    if (step === 3) {
      const first = getFirstAvailableDate();
      if (first) setDisplayedMonth(startOfMonth(first));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const modifiers = {
    disabled: (d) => isDayDisabled(d),
    available: (d) => !isDayDisabled(d),
  };
  const modifiersClassNames = {
    disabled: "invalid-day",
    available: "available-day",
  };

  const selectWorker = (id) => {
    if (id !== selectedWorker) {
      setSelectedWorker(id);
      setSelectedServiceId(null);
      setSelectedServiceDuration(30);
      setSelectedDate(null);
      setSelectedHour(null);
      setAvailableHours([]);
      setFullyBookedDates([]);
      setBookings([]);
    }
  };

  const selectService = (svc) => {
    setSelectedServiceId(svc.id);
    setSelectedServiceDuration(svc.duration || 30);
    setSelectedHour(null);
  };

  const confirmReserva = () => {
    setLoading(true);
    setConfirmError(null);
    const dateForApi = format(selectedDate, "yyyy-MM-dd");
    const bookingData = {
      datetime_init: `${dateForApi} ${selectedHour}`,
      client_name: form.name,
      client_tlf: form.number,
      client_email: form.email,
      services_ids: [String(selectedServiceId)],
      worker_id: selectedWorker,
    };
    apiPost(`/booking/local/${LOCAL}`, bookingData, {
      context: "reservas_confirmar",
      retryOn500: true,
    })
      .then((response) => {
        setBookingDetails(response.data);
        setStep(7);
      })
      .catch((error) => {
        console.error("Error al realizar la reserva:", error);
        const status = error?.response?.status;
        if (!error.response) {
          setConfirmError(
            "No se ha podido conectar con el sistema de reservas. Comprueba tu conexión (prueba a cambiar entre WiFi y datos) e inténtalo de nuevo. Si sigue fallando, escríbenos por WhatsApp y te guardamos la cita.",
          );
        } else if (status === 409 || status === 400) {
          setConfirmError(
            "No se ha podido confirmar la reserva: es posible que esa hora se acabe de ocupar. Vuelve atrás y elige otra hora.",
          );
          fetchReservations(selectedDate, selectedWorker);
        } else {
          setConfirmError(
            `Ha ocurrido un error al confirmar la reserva (código ${status}). Inténtalo de nuevo en unos minutos o escríbenos por WhatsApp.`,
          );
        }
      })
      .finally(() => setLoading(false));
  };

  const formValid =
    form.name.trim().length >= 3 &&
    /^\S+@\S+\.\S+$/.test(form.email) &&
    /^\d{9,13}$/.test(form.number);

  const longDate = selectedDate
    ? capitalize(format(selectedDate, "EEEE d 'de' MMMM", { locale: es }))
    : "";

  const canContinue =
    (step === 1 && !!selectedWorker) ||
    (step === 2 && !!selectedServiceId) ||
    (step === 3 && !!selectedDate) ||
    (step === 4 && !!selectedHour) ||
    (step === 5 && formValid);

  const stepTitles = {
    1: ["¿Con quién quieres reservar?", "Elige el profesional"],
    2: ["¿Qué servicio necesitas?", "Elige el servicio"],
    3: ["Elige el día", `Días disponibles con ${selectedWorkerObj?.name}`],
    4: ["Elige la hora", `${longDate} con ${selectedWorkerObj?.name}`],
    5: ["Tus datos", "Rellena tus datos para confirmar la reserva"],
    6: ["Resumen de tu reserva", "Revisa los detalles y confirma"],
  };

  // ---------- step renderers ----------
  const renderProfesional = () => (
    <div className="rv-list">
      {workers.map((w) => (
        <button
          key={w.id}
          type="button"
          className={`rv-worker${selectedWorker === w.id ? " is-sel" : ""}`}
          onClick={() => selectWorker(w.id)}
        >
          {Avatar(w)}
          <span className="rv-worker-info">
            <span className="rv-worker-name">{w.name}</span>
            <span className="rv-worker-role">{w.role}</span>
            <span className="rv-worker-next">
              Próxima cita:{" "}
              {firstAvailByWorker[w.id] === undefined ? (
                <span className="rv-mini-spinner" aria-label="cargando" />
              ) : firstAvailByWorker[w.id] ? (
                <b>{longDateOf(firstAvailByWorker[w.id])}</b>
              ) : (
                <b>—</b>
              )}
            </span>
          </span>
          <span className="rv-radio" aria-hidden="true" />
        </button>
      ))}
    </div>
  );

  const renderServicio = () => (
    <div className="rv-services">
      {servicesError && (
        <div className="rv-error" role="alert">
          <p>
            No se han podido cargar los servicios. Comprueba tu conexión e
            inténtalo de nuevo.
          </p>
          <button
            type="button"
            className="rv-btn rv-btn--ghost rv-error-retry"
            onClick={() => getServices(selectedWorker)}
          >
            Reintentar
          </button>
        </div>
      )}
      {haircutTypes.map((s) => (
        <button
          key={s.id}
          type="button"
          className={`rv-service${selectedServiceId === s.id ? " is-sel" : ""}`}
          onClick={() => selectService(s)}
        >
          <span className="rv-ico">
            <IconScissors />
          </span>
          <span className="rv-service-info">
            <span className="rv-service-name">{s.name}</span>
            <span className="rv-service-dur">{s.duration || 30} min</span>
          </span>
          <span className="rv-service-price">{s.price} €</span>
        </button>
      ))}
    </div>
  );

  const renderDia = () => (
    <div className="rv-calendar-wrap">
      {(loadingDisabledDates || isLoadingMonth) && (
        <div className="rv-cal-overlay">
          <div className="rv-spinner" />
        </div>
      )}
      <div
        className="rv-calendar"
        onWheelCapture={(e) => e.stopPropagation()}
        onTouchMoveCapture={(e) => e.stopPropagation()}
      >
        <DatePickerCalendar
          date={selectedDate}
          month={displayedMonth}
          onMonthChange={(d) => setDisplayedMonth(startOfMonth(d))}
          onDateChange={handleDateChange}
          modifiers={modifiers}
          modifiersClassNames={modifiersClassNames}
          locale={es}
        />
      </div>
      <div className="rv-legend">
        <span>
          <i className="rv-dot rv-dot--free" /> Disponible
        </span>
        <span>
          <i className="rv-dot rv-dot--busy" /> No disponible
        </span>
      </div>
      {(() => {
        const other = workers.find((w) => w.id !== selectedWorker);
        const otherDate = other && firstAvailByWorker[other.id];
        const selfDate = firstAvailByWorker[selectedWorker];
        if (!other || !otherDate) return null;
        if (selfDate && !isBefore(otherDate, selfDate)) return null;
        return (
          <button
            type="button"
            className="rv-otherbarber"
            onClick={() => {
              selectWorker(other.id);
              setDisplayedMonth(startOfMonth(otherDate));
            }}
          >
            <div className="rv-otherbarber-top">
              {Avatar(other, true)}
              <span className="rv-otherbarber-info">
                <span className="rv-otherbarber-name">
                  {other.name} tiene disponibilidad antes
                </span>
                <span className="rv-otherbarber-date">{longDateOf(otherDate)}</span>
              </span>
            </div>
            <span className="rv-otherbarber-cta">
              Ver disponibilidad de {other.name.split(" ")[0]} &rarr;
            </span>
          </button>
        );
      })()}
    </div>
  );

  const renderHora = () => (
    <div className="rv-hours-wrap">
      {loading ? (
        <div className="rv-spinner" />
      ) : hoursError ? (
        <div className="rv-error" role="alert">
          <p>
            No se han podido cargar las horas disponibles. Comprueba tu
            conexión e inténtalo de nuevo.
          </p>
          <button
            type="button"
            className="rv-btn rv-btn--ghost rv-error-retry"
            onClick={() => fetchReservations(selectedDate, selectedWorker)}
          >
            Reintentar
          </button>
        </div>
      ) : availableHours.length > 0 ? (
        <>
          <div className="rv-hours">
            {availableHours.map((h) => (
              <button
                key={h}
                type="button"
                className={`rv-hour${selectedHour === h ? " is-sel" : ""}`}
                onClick={() => setSelectedHour(h)}
              >
                {h}
              </button>
            ))}
          </div>
          <div className="rv-duration">
            <IconClock />
            Duración del servicio: {selectedServiceDuration} min
          </div>
        </>
      ) : (
        <p className="rv-empty">
          No hay horas disponibles para este día. Vuelve atrás y elige otra
          fecha.
        </p>
      )}
    </div>
  );

  const renderFormulario = () => (
    <div className="rv-form">
      <label className="rv-field">
        <IconUser />
        <input
          type="text"
          placeholder="Nombre y apellidos"
          value={form.name}
          minLength={3}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
      </label>
      <label className="rv-field">
        <IconMail />
        <input
          type="email"
          placeholder="Email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
      </label>
      <label className="rv-field">
        <IconPhone />
        <input
          type="tel"
          placeholder="Teléfono"
          value={form.number}
          onChange={(e) =>
            setForm({ ...form, number: e.target.value.replace(/[^\d]/g, "") })
          }
        />
      </label>
      <p className="rv-field-note">
        Te enviaremos la confirmación y un recordatorio a tu email.
      </p>
    </div>
  );

  const renderResumen = () => (
    <div className="rv-summary">
      <div className="rv-sum-row">
        {Avatar(selectedWorkerObj, true)}
        <div>
          <span className="rv-sum-label">{selectedWorkerObj?.role}</span>
          <span className="rv-sum-value">{selectedWorkerObj?.name}</span>
        </div>
      </div>
      <div className="rv-sum-row">
        <span className="rv-ico sm"><IconScissors /></span>
        <div>
          <span className="rv-sum-label">Servicio</span>
          <span className="rv-sum-value">{selectedService?.name}</span>
        </div>
      </div>
      <div className="rv-sum-row">
        <span className="rv-ico sm"><IconClock /></span>
        <div>
          <span className="rv-sum-label">Duración</span>
          <span className="rv-sum-value">{selectedServiceDuration} min</span>
        </div>
      </div>
      <div className="rv-sum-row">
        <span className="rv-ico sm"><IconCalendar /></span>
        <div>
          <span className="rv-sum-label">Fecha</span>
          <span className="rv-sum-value">{longDate}</span>
        </div>
      </div>
      <div className="rv-sum-row">
        <span className="rv-ico sm"><IconClock /></span>
        <div>
          <span className="rv-sum-label">Hora</span>
          <span className="rv-sum-value">{selectedHour}</span>
        </div>
      </div>
      <div className="rv-sum-row">
        <span className="rv-ico sm"><IconTag /></span>
        <div>
          <span className="rv-sum-label">Precio</span>
          <span className="rv-sum-value">{selectedService?.price} €</span>
        </div>
      </div>
    </div>
  );

  const renderConfirmada = () => (
    <div className="rv-confirmed">
      <span className="rv-check">
        <IconCheckCircle />
      </span>
      <h2 className="rv-confirmed-title">¡Reserva confirmada!</h2>
      <p className="rv-confirmed-sub">Hemos enviado los detalles a tu email.</p>
      <div className="rv-summary rv-summary--compact">
        <div className="rv-sum-row">
          <span className="rv-ico sm"><IconCalendar /></span>
          <div>
            <span className="rv-sum-label">Fecha</span>
            <span className="rv-sum-value">{longDate}</span>
          </div>
        </div>
        <div className="rv-sum-row">
          <span className="rv-ico sm"><IconClock /></span>
          <div>
            <span className="rv-sum-label">Hora</span>
            <span className="rv-sum-value">{selectedHour}</span>
          </div>
        </div>
        <div className="rv-sum-row">
          <span className="rv-ico sm"><IconScissors /></span>
          <div>
            <span className="rv-sum-label">Servicio</span>
            <span className="rv-sum-value">{selectedService?.name}</span>
          </div>
        </div>
        <div className="rv-sum-row">
          {Avatar(selectedWorkerObj, true)}
          <div>
            <span className="rv-sum-label">{selectedWorkerObj?.role}</span>
            <span className="rv-sum-value">{selectedWorkerObj?.name}</span>
          </div>
        </div>
      </div>
      <div className="rv-reminder">
        <IconBell />
        Recibirás un correo con la información en los próximos 10 minutos.
      </div>
      <Link to="/" className="rv-btn rv-btn--primary">
        Aceptar
      </Link>
    </div>
  );

  const renderStep = () => {
    switch (step) {
      case 1:
        return renderProfesional();
      case 2:
        return renderServicio();
      case 3:
        return renderDia();
      case 4:
        return renderHora();
      case 5:
        return renderFormulario();
      case 6:
        return renderResumen();
      default:
        return null;
    }
  };

  return (
    <div className="rv">
      <header className="rv-bar">
        <Link to="/" className="rv-back" aria-label="Volver al inicio">
          <IconArrowLeft />
        </Link>
        <span className="rv-bar-title">SUBLIME HAIR SALON</span>
        <a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="rv-wa"
          aria-label="Contactar por WhatsApp"
        >
          <svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
            <circle cx="24" cy="24" r="22" fill="none" stroke="#fff" strokeWidth="1.3" />
            <IconWhatsApp x="13" y="11.5" width="22" height="25" />
          </svg>
        </a>
      </header>

      <main className="rv-main">
        {step <= 6 && (
          <div className="rv-stepper">
            {STEPS.map((label, i) => {
              const n = i + 1;
              const done = n < step;
              const active = n === step;
              return (
                <div
                  key={label}
                  className={`rv-step${active ? " is-active" : ""}${
                    done ? " is-done" : ""
                  }`}
                >
                  <span className="rv-step-dot">
                    {done ? <IconCheck /> : n}
                  </span>
                  <span className="rv-step-label">{label}</span>
                </div>
              );
            })}
          </div>
        )}

        {step <= 6 ? (
          <div className="rv-card">
            <div className="rv-head">
              <h1 className="rv-title">{stepTitles[step][0]}</h1>
              <p className="rv-sub">{stepTitles[step][1]}</p>
            </div>

            <div className="rv-body">{renderStep()}</div>

            {step === 6 && confirmError && (
              <div className="rv-error" role="alert">
                <p>{confirmError}</p>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rv-btn rv-btn--ghost rv-error-retry"
                >
                  Escribir por WhatsApp
                </a>
              </div>
            )}

            <div className="rv-actions">
              {step < 6 ? (
                <button
                  type="button"
                  className="rv-btn rv-btn--primary"
                  disabled={!canContinue}
                  onClick={() => setStep(step + 1)}
                >
                  Continuar
                </button>
              ) : (
                <button
                  type="button"
                  className="rv-btn rv-btn--primary"
                  disabled={loading}
                  onClick={confirmReserva}
                >
                  {loading ? "Confirmando..." : "Confirmar reserva"}
                </button>
              )}
              {step > 1 ? (
                <button
                  type="button"
                  className="rv-btn rv-btn--ghost"
                  onClick={() => {
                    setConfirmError(null);
                    setStep(step - 1);
                  }}
                >
                  Volver atrás
                </button>
              ) : (
                <Link to="/" className="rv-btn rv-btn--ghost">
                  Cancelar
                </Link>
              )}
            </div>
          </div>
        ) : (
          <div className="rv-card">{renderConfirmada()}</div>
        )}
      </main>
    </div>
  );
}

export default Reservas;
